import Flicking from '@egjs/react-flicking';
import styled from '@emotion/styled';
import { Masonry } from '@mui/lab';
import {
  Button as MuiButton,
  Divider,
  Grid,
  IconButton,
  Switch,
  useMediaQuery,
} from '@mui/material';
import Box from '@mui/material/Box';
import { gql } from 'graphql-request';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState } from 'react';
import { setTimeout } from 'timers';

import type { GetCampsiteIndexQuery } from '@/__generated__/public-graphql';
import { useGetCampsiteIndexQuery } from '@/__generated__/public-graphql';
import {
  useAddVisitedMutation,
  useCheckVisitedByCampsiteIdQuery,
  useDeleteCampSiteLikeMutation,
  useRemoveVisitedMutation,
} from '@/__generated__/user-graphql';
import BeforeEditInfo from '@/contents/CampSite/camp/BeforeEditInfo';
import BeforeUploadPhoto from '@/contents/CampSite/camp/BeforeUploadPhoto';
import { ConstSearch } from '@/contents/constants';
import constBath from '@/contents/constants/constBath';
import constBusinessPeriod from '@/contents/constants/constBusinessPeriod';
import constMeta from '@/contents/constants/constMeta';
import Button from '@/contents/General/Button';
import Icon from '@/contents/General/Icon';
import Image from '@/contents/General/Image';
import Stack from '@/contents/General/Stack';
import Typography from '@/contents/General/Typography';
import { GlobalContext } from '@/contents/hooks/state';
import Layout from '@/layouts';
import CampSiteHistory from '@/modules/CampSite/CampSiteHistory';
import getCampSiteIdsOnStaticPaths from '@/modules/CampSite/getCampSiteIdsOnStaticPaths';
import getCampSiteOnStaticProps from '@/modules/CampSite/getCampSiteOnStaticProps';
import useCountUpCampSiteAccessCount from '@/modules/CampSite/useCountUpCampSiteAccessCount';
import useCampSiteLikeFromPublic from '@/modules/CampSiteLikeList/public/useCampSiteLikeFromPublic';
import OptimizedImage from '@/modules/General/OptimizedImage';
import useGetLoginUser from '@/modules/User/user/useGetLoginUser';
import theme from '@/styles/theme';
import type { NextPageWithLayout } from '@/types';
import { Page } from '@/types/domain';
import { isAndroid } from '@/utils/getDevice';
import getRevalidateSec from '@/utils/getRevalidateSec';
import ImageUrlConvert from '@/utils/ImageUrlConvert';
import prefecturesCodeToString from '@/utils/prefecturesCodeToString';

const Container = styled(Box)`
  width: 100%;
  height: 100%;
  max-width: inherit;
` as typeof Box;

const Category = styled(Box)<{ is_pc: string }>`
  position: fixed;
  overflow-x: auto;
  padding: ${theme.spacing(2)} ${theme.spacing(3)};
  width: 100vw;
  z-index: 1000;
  background: ${(props) =>
    props.is_pc
      ? 'unset'
      : 'linear-gradient(to bottom, rgba(51, 51, 51, 0.6), transparent)'};
  ${(props) => props.is_pc && `max-width: 506px;`}
  ${(props) => props.is_pc && `border-radius: 40px 40px 0 0;`}
  p {
    display: flex;
    flex-direction: column;
    align-items: center;
    white-space: nowrap;
    cursor: pointer;
    &::after {
      content: '';
      margin-top: 4px;
      width: 18px;
      height: 4px;
      background: url('/assets/images/icons/icon_arrowbottom_small.svg');
    }
    &:last-of-type {
      padding-right: ${theme.spacing(3)};
    }
  }
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ImageNotEntered = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 90vh;
  background: url('/assets/images/common/bg_lg.jpg');
  background-size: cover;
`;

const Footer = styled(Box)`
  position: fixed;
  bottom: 0;
  padding-top: 120px;
  width: inherit;
  max-width: inherit;
  max-height: 100%;
  border-radius: 16px 16px 0 0;
  z-index: 13;
`;

const Navi = styled(Box)`
  position: absolute;
  right: ${theme.spacing(3)};
  margin-top: -80px;
  width: 90%;
`;

const DetailArea = styled(Box)`
  display: flex;
  flex-direction: column;
  min-height: 148px;
  border-radius: 16px 16px 0 0;
  background: #f4f8f6;
  transition: height ease-in-out 0.2s;
` as typeof Box;

const Detail = styled(Box)`
  position: relative;
  bottom: 82px;
  overflow-y: scroll;
`;

const AreaName = styled(Box)`
  display: flex;
  align-items: center;
  padding: 0px 8px;
  width: fit-content;
  height: 29px;
  border-radius: 14px;
  background: #e2edf5;
`;

const Preview = styled(Box)`
  img {
    width: 100%;
    height: calc((100vw - 48px) * 0.75);
    max-height: 214px;
    border-radius: 8px;
    object-fit: cover;
  }
`;

const InfoNotEntered = styled(Box)`
  width: 100%;
  height: calc((100vw - 48px) * 0.75);
  max-height: 343px;
  border-radius: 8px;
  background: url('/assets/images/common/bg_nophoto.webp') no-repeat;
  background-size: cover;
`;

const Item = styled(Box)<{ checked: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${(props) => (props.checked ? '#E2EDF5' : '#e3e9e6')};
`;

const TransparentButton = styled(MuiButton)`
  padding: 0 10px;
  height: 29px;
  color: #ffffff;
  font-size: 14px;
  border-radius: 15px;
  background: rgba(51, 51, 51, 0.6);
`;

type CampSite = NonNullable<GetCampsiteIndexQuery['campsite_by_pk']>;
type Props = {
  queryResult: CampSite;
  path: string;
};

const getImageByType = (
  campSite: CampSite,
  type: string
): {
  alt: string;
  created_at: string;
  file_name: string;
  title: string;
  user_id: string;
  id: string;
  image_id: string;
}[] => {
  return (
    campSite.campsite_images
      .filter((image) => image.type === type)
      .map((campSiteImage) => ({
        alt: campSiteImage.image.alt,
        created_at: campSiteImage.image.created_at,
        file_name: campSiteImage.image.file_name,
        title: campSiteImage.image.title,
        user_id: campSiteImage.image.user_id ?? '',
        id: campSiteImage.image.id,
        image_id: campSiteImage.id,
      })) ?? []
  );
};
const Index: NextPageWithLayout<Props> = ({ queryResult, path }) => {
  const { data, refetch } = useGetCampsiteIndexQuery(
    {
      id: queryResult.id,
    },
    {
      initialData: { campsite_by_pk: queryResult },
    }
  );

  const campSite = data?.campsite_by_pk!;
  const campSiteSiteImages = getImageByType(campSite, 'campsite');
  const campSiteFacilityImages = getImageByType(campSite, 'facility');
  const campSiteViewImages = getImageByType(campSite, 'view');
  const campSiteOtherImages = getImageByType(campSite, 'other');
  const router = useRouter();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { user, isLogin, isFetched } = useGetLoginUser();
  const { setPage, isLoading, setIsLoading, dialogStatus, setDialogStatus } =
    useContext(GlobalContext);
  const [isShow, setIsShow] = useState(false);
  const [favorite, setFavorite] = useState(false);
  const { data: allCampSiteLikeList, isLoading: campSiteLikeListLoading } =
    useCampSiteLikeFromPublic(user?.id ?? '', {
      enabled: !!user?.id,
    });
  const isPC = useMediaQuery('(min-width:900px)');
  const isLikedByUser = !!allCampSiteLikeList?.all.find(
    (item) => item.campsite_id === campSite.id
  );

  useCountUpCampSiteAccessCount(campSite.id);
  const { data: visitedData, refetch: visitedRefetch } =
    useCheckVisitedByCampsiteIdQuery(
      {
        campsite_id: campSite.id,
        user_id: user?.id ?? '',
      },
      {
        enabled: isLogin,
      }
    );
  // お気に入り・行った登録時に通知を再取得
  const triggerGetNotification = () => {
    router.push(
      {
        pathname: '/camp/[id]',
        query: { id: campSite.id },
      },
      undefined,
      { shallow: true }
    );
  };

  useEffect(() => {
    setFavorite(isLikedByUser);
    if (isLikedByUser) {
      // おきにいり登録時に通知を再取得
      triggerGetNotification();
    }
  }, [isLikedByUser]);

  const changeIsShow = (show: boolean) => {
    if (!show) {
      document.body.style.position = 'fixed';
      document.body.style.overflow = 'hidden';
      document.body.style.inset = '0px';
    } else {
      document.body.removeAttribute('style');
      if (scrollRef && scrollRef.current) {
        scrollRef.current.scrollTo(0, 0);
      }
    }
    setIsShow(!show);
  };

  // const slideImages = [
  //   ...(campSite.campSiteImages ?? []),
  //   ...(campSite.facilityImages ?? []),
  //   ...(campSite.viewImages ?? []),
  //   ...(campSite.otherImages ?? []),
  // ].map((image) => {
  //   return {
  //     userId: image.userId,
  //     imageUrl: ImageUrlConvert(image.file_name, true),
  //     title: image.title,
  //   }
  // })
  const slideImages = [
    ...campSiteSiteImages,
    ...campSiteFacilityImages,
    ...campSiteViewImages,
    ...campSiteOtherImages,
  ].map((image) => ({
    userId: image.user_id,
    imageUrl: ImageUrlConvert(image.file_name, true),
    title: image.title,
    imageId: image.image_id,
  }));

  const getImagesId = (index: number): string => {
    const siteImageLength = campSiteSiteImages?.length ?? 0;
    const facilityImageLength = campSiteFacilityImages?.length ?? 0;
    const viewImageLength = campSiteViewImages?.length ?? 0;
    if (index === 0) {
      return 'site-image';
    }
    if (index === siteImageLength) {
      return 'facility-image';
    }
    if (index === siteImageLength + facilityImageLength) {
      return 'view-image';
    }
    if (index === siteImageLength + facilityImageLength + viewImageLength) {
      return 'other-image';
    }
    return '';
  };

  const scrollImage = (imageId: string) => {
    if (process.browser) {
      const targetEl = document.querySelector(`#${imageId}`);
      const offsetY = targetEl?.getBoundingClientRect().top;
      window?.scrollTo({
        top: window.pageYOffset + (offsetY as number) - 60,
        behavior: 'smooth',
      });
    }
  };

  const openImageSlide = (index: number) => {
    setDialogStatus({
      open: true,
      type: 'CAMP_IMAGE_SLIDE',
      props: { slideList: slideImages, index },
    });
    changeIsShow(true);
  };

  const openEditDialog = () => {
    setDialogStatus({
      open: true,
      type: 'COMMON',
      props: {},
      children: <BeforeEditInfo campSiteId={campSite.id} />,
    });
    changeIsShow(true);
  };

  const openUploadDialog = () => {
    setDialogStatus({
      open: true,
      type: 'COMMON',
      props: {},
      children: <BeforeUploadPhoto campSiteId={campSite.id} />,
    });
  };

  const nextPage = () => {
    router.push(`/camp/${campSite.id}/log`);
  };

  useEffect(() => {
    setPage(Page.CAMPSITE);
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  const CheckBoxList = ({
    items,
  }: {
    items: { label: string; value: string }[];
  }) => {
    return (
      <Grid container spacing={2} paddingTop={2}>
        {items.map((item, index) => {
          // eslint-disable-next-line no-prototype-builtins
          if (!campSite.hasOwnProperty(item.value)) {
            return <></>;
          }
          const checked =
            // @ts-ignore
            (campSite[item.value] ?? false) === true;
          return (
            <Grid item xs={6} key={item.value + index}>
              <Item checked={checked}>
                <Typography fontSize={14} fontWeight={500}>
                  {item.label}
                </Typography>
                {checked ? (
                  <Icon iconType="circle" />
                ) : (
                  <Icon iconType="minus" />
                )}
              </Item>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <>
      <Head>
        <title>{`${campSite.name}《${prefecturesCodeToString(
          campSite.prefectures_code
        )}》${constMeta.camp.title}`}</title>
        <meta
          name="description"
          content={`${campSite.name}《${prefecturesCodeToString(
            campSite.prefectures_code
          )}》${constMeta.camp.mata_description}`}
        />
        <meta property="og:url" content={`https://iihi.life/camp/${path}`} />
        <meta
          property="og:title"
          content={`${campSite.name}《${prefecturesCodeToString(
            campSite.prefectures_code
          )}》${constMeta.camp.title}`}
        />
        <meta
          property="og:description"
          content={`${campSite.name}《${prefecturesCodeToString(
            campSite.prefectures_code
          )}》${constMeta.camp.mata_description}`}
        />
        <meta
          property="og:image"
          content={
            campSite.image
              ? ImageUrlConvert(campSite.image.file_name, true)
              : 'https://iihi.life/assets/images/common/ogp.jpg'
          }
        />
      </Head>
      <Container
        sx={{ opacity: isLoading ? 0 : 1, transition: 'opacity ease 0.6s' }}
      >
        <Box>
          <Category is_pc={isPC ? isPC.toString() : ''}>
            <Stack space={3}>
              <Typography
                onClick={() => scrollImage('site-image')}
                fontSize={14}
                fontWeight={500}
                color="white"
              >
                サイト・宿泊施設({campSiteSiteImages.length ?? 0})
              </Typography>
              <Typography
                onClick={() => scrollImage('facility-image')}
                fontSize={14}
                fontWeight={500}
                color="white"
              >
                場内施設({campSiteFacilityImages?.length ?? 0})
              </Typography>
              <Typography
                onClick={() => scrollImage('view-image')}
                fontSize={14}
                fontWeight={500}
                color="white"
              >
                景色({campSiteViewImages.length ?? 0})
              </Typography>
              <Typography
                onClick={() => scrollImage('other-image')}
                fontSize={14}
                fontWeight={500}
                color="white"
              >
                その他({campSiteOtherImages?.length ?? 0})
              </Typography>
            </Stack>
          </Category>
          {slideImages.length > 0 ? (
            <Masonry columns={2} spacing={0.3}>
              {slideImages.map((item, index) => (
                <Box
                  id={getImagesId(index)}
                  key={item.imageUrl + index}
                  onClick={() => openImageSlide(index)}
                  sx={{
                    cursor: 'pointer',
                    '& :hover': {
                      opacity: 0.6,
                    },
                  }}
                >
                  <input type="hidden" value={item.imageId} name="image_id" />
                  <Image
                    src={`${item.imageUrl}`}
                    width="100%"
                    height="100%"
                    alt={item.title ?? ''}
                  />
                </Box>
              ))}
            </Masonry>
          ) : (
            <ImageNotEntered>
              <Stack center column paddingBottom={'20vh'}>
                <Typography
                  fontSize={16}
                  fontWeight={700}
                  color="white"
                  paddingBottom={2}
                >
                  現在、写真がまだありません！
                </Typography>
                <Typography
                  fontSize={28}
                  fontWeight={500}
                  color="white"
                  paddingBottom={3}
                >
                  写真募集中
                </Typography>
                <Button
                  onClick={openUploadDialog}
                  buttonSize="md"
                  color="secondary"
                  sx={{ width: '246px' }}
                >
                  写真を登録する
                </Button>
              </Stack>
            </ImageNotEntered>
          )}
        </Box>
        <Footer>
          {!isShow && (
            <Navi>
              <Stack between>
                <Box
                  sx={{
                    maxHeight: '64px',
                    width: '64px',
                    height: '64px',
                  }}
                >
                  {campSite.is_affiliated ? (
                    <OptimizedImage
                      src="/assets/images/common/is_affliatedLabel.png"
                      alt="提携キャンプ場"
                      width="100%"
                      height="100%"
                    />
                  ) : (
                    <></>
                  )}
                </Box>
                <Stack space={1} alignEnd>
                  <TransparentButton onClick={openEditDialog}>
                    情報編集
                  </TransparentButton>
                  <TransparentButton onClick={openUploadDialog}>
                    写真登録
                  </TransparentButton>
                </Stack>
              </Stack>
            </Navi>
          )}
          <DetailArea
            sx={{
              maxHeight: '90vh',
              height: isShow ? '82vh' : '180px',
            }}
          >
            <Flicking
              horizontal={false}
              onHoldEnd={() => changeIsShow(isShow)}
              onClick={() => isAndroid() && changeIsShow(isShow)}
              style={{ zIndex: 10 }}
            >
              <Box>
                <Stack
                  justifyCenter
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '102px',
                  }}
                >
                  <IconButton
                    sx={{
                      paddingTop: '4px',
                      height: 'fit-content',
                      willChange: 'animation',
                      animation: 'arrowmove 1.2s ease infinite',
                      '&:hover': {
                        backgroundColor: 'unset',
                      },
                      pointerEvents: 'none',
                    }}
                  >
                    <Icon
                      iconType={
                        isShow ? 'arrow-bottom-double' : 'arrow-top-double'
                      }
                    />
                  </IconButton>
                </Stack>
              </Box>
            </Flicking>
            <Detail ref={scrollRef}>
              <Box sx={{ padding: `0 ${theme.spacing(3)}` }}>
                <Typography
                  fontSize={21}
                  fontWeight={700}
                  sx={{ paddingBottom: theme.spacing(1) }}
                  ellipsis={!isShow ? 1 : undefined}
                  variant="h1"
                >
                  {campSite.name}
                </Typography>
                <Stack
                  alignCenter
                  space={1.5}
                  sx={{ paddingBottom: theme.spacing(2) }}
                >
                  <AreaName>
                    <Typography
                      fontSize={14}
                      fontWeight={500}
                      color="primary"
                      whiteSpace="nowrap"
                    >
                      {prefecturesCodeToString(campSite.prefectures_code)}
                    </Typography>
                  </AreaName>
                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    ellipsis={!isShow ? 1 : undefined}
                  >
                    {campSite.address}
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{ overflowY: isShow ? 'auto' : 'hidden' }}>
                <Box sx={{ padding: `0 ${theme.spacing(3)}` }}>
                  <Divider />
                  <Box sx={{ padding: `${theme.spacing(3)} 0` }}>
                    {campSite.image ? (
                      <Preview>
                        <Image
                          src={ImageUrlConvert(campSite.image.file_name, true)}
                          width="100%"
                          height="100%"
                          alt={campSite.name ?? ''}
                        />
                      </Preview>
                    ) : (
                      <InfoNotEntered>
                        <Stack center column sx={{ height: '100%' }}>
                          <Typography
                            fontSize={16}
                            fontWeight={700}
                            color="white"
                            paddingBottom={3}
                            textAlign="center"
                          >
                            現在、メインビジュアル(サムネイル)
                            <br />
                            がまだありません！
                          </Typography>
                          <Button
                            onClick={openEditDialog}
                            buttonSize="md"
                            color="secondary"
                            sx={{ width: '246px' }}
                          >
                            写真を設定する
                          </Button>
                        </Stack>
                      </InfoNotEntered>
                    )}
                  </Box>
                  {campSite.introduction_title && (
                    <Typography
                      fontSize={18}
                      fontWeight={700}
                      sx={{ paddingBottom: theme.spacing(2) }}
                    >
                      {campSite.introduction_title ?? ''}
                    </Typography>
                  )}
                  {campSite.introduction_content && (
                    <Typography fontSize={15}>
                      {campSite.introduction_content ?? ''}
                    </Typography>
                  )}
                  {campSite.official_url && (
                    <Stack center paddingTop={5}>
                      <a
                        aria-disabled={!campSite.official_url}
                        href={`${campSite.official_url}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Button
                          buttonSize="md"
                          color="secondary"
                          endIcon={<Icon iconType="link" color="white" />}
                          sx={{ width: '246px' }}
                        >
                          公式・関連サイトから予約確認
                        </Button>
                      </a>
                    </Stack>
                  )}
                  <Typography
                    fontSize={18}
                    fontWeight={700}
                    textAlign="center"
                    paddingTop={5}
                    paddingBottom={3}
                  >
                    基本情報
                  </Typography>
                  <Box paddingBottom={5}>
                    <Typography
                      fontSize={16}
                      fontWeight={700}
                      paddingBottom={1}
                      variant="h2"
                    >
                      施設タイプ
                    </Typography>
                    <Divider />
                    <CheckBoxList items={ConstSearch.equipment} />
                  </Box>
                  <Box paddingBottom={5}>
                    <Typography
                      fontSize={16}
                      fontWeight={700}
                      paddingBottom={1}
                      variant="h2"
                    >
                      ロケーション
                    </Typography>
                    <Divider />
                    <CheckBoxList items={ConstSearch.location} />
                  </Box>
                  <Box paddingBottom={5}>
                    <Typography
                      fontSize={16}
                      fontWeight={700}
                      paddingBottom={1}
                      variant="h2"
                    >
                      車両乗り入れ
                    </Typography>
                    <Divider />
                    <CheckBoxList items={ConstSearch.carStock} />
                  </Box>
                  <Box paddingBottom={5}>
                    <Stack alignEnd space={1} paddingBottom={1}>
                      <Typography fontSize={16} fontWeight={700} variant="h2">
                        営業期間・時間
                      </Typography>
                      <Typography fontSize={12} fontWeight={500}>
                        ※テントサイトを基準
                      </Typography>
                    </Stack>
                    <Divider />
                    <Grid container spacing={2} paddingTop={2}>
                      {constBusinessPeriod.map((item, index) => {
                        const checked =
                          // @ts-ignore
                          (campSite[item.value] ?? false) === true;
                        return (
                          <Grid item xs={6} key={item.value + index}>
                            <Item checked={checked}>
                              <Typography fontSize={14} fontWeight={500}>
                                {item.label}
                              </Typography>
                              {checked ? (
                                <Icon iconType="circle" />
                              ) : (
                                <Icon iconType="minus" />
                              )}
                            </Item>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                  <Grid container spacing={2} paddingBottom={5}>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={4.1}>
                      <Typography fontSize={14} fontWeight={700} variant="h2">
                        定休日
                      </Typography>
                    </Grid>
                    <Grid item xs={7.9}>
                      <Typography fontSize={14} fontWeight={500}>
                        {campSite.closed_not ||
                        campSite.closed_irregular ||
                        campSite.closed_holiday ||
                        campSite.closed_monday ||
                        campSite.closed_tuesday ||
                        campSite.closed_wednesday ||
                        campSite.closed_thursday ||
                        campSite.closed_friday ||
                        campSite.closed_saturday ||
                        campSite.closed_sunday
                          ? [
                              campSite.closed_not && '冬季休業',
                              campSite.closed_irregular && '不定休',
                              campSite.closed_holiday && '祝日',
                              campSite.closed_monday && '月曜日',
                              campSite.closed_tuesday && '火曜日',
                              campSite.closed_wednesday && '水曜日',
                              campSite.closed_thursday && '木曜日',
                              campSite.closed_friday && '金曜日',
                              campSite.closed_saturday && '土曜日',
                              campSite.closed_sunday && '日曜日',
                            ]
                              .filter((item) => !!item)
                              .join('、')
                          : '年中無休'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    {/* チェックイン時間が空文字の場合は項目ごと表示しない */}
                    {/* チェックイン時間が-の場合、特に規定なし */}
                    {campSite.checkin_start_hour !== '' &&
                      campSite.checkin_start_minute !== '' && (
                        <>
                          <Grid item xs={4.1}>
                            <Typography
                              fontSize={14}
                              fontWeight={700}
                              variant="h2"
                            >
                              チェックイン
                            </Typography>
                          </Grid>
                          <Grid item xs={7.9}>
                            {campSite.checkin_start_hour === '--' &&
                            campSite.checkin_start_minute === '--' &&
                            campSite.checkin_end_hour === '--' &&
                            campSite.checkin_end_minute === '--' ? (
                              <Typography fontSize={14} fontWeight={500}>
                                インフリー
                              </Typography>
                            ) : (
                              <Stack>
                                {campSite.checkin_start_hour !== '--' && (
                                  <Typography
                                    fontSize={14}
                                    fontWeight={500}
                                    width={40}
                                  >
                                    {campSite.checkin_start_hour}:
                                    {campSite.checkin_start_minute}
                                  </Typography>
                                )}
                                <Typography
                                  fontSize={14}
                                  fontWeight={500}
                                  margin={`0 ${theme.spacing(1)}`}
                                >
                                  〜
                                </Typography>
                                {campSite.checkin_end_hour !== '--' && (
                                  <Typography
                                    fontSize={14}
                                    fontWeight={500}
                                    width={40}
                                  >
                                    {campSite.checkin_end_hour}:
                                    {campSite.checkin_end_minute}
                                  </Typography>
                                )}
                              </Stack>
                            )}
                          </Grid>
                        </>
                      )}
                    {/* チェックアウト時間が空文字の場合は項目ごと表示しない */}
                    {/* チェックアウト時間が-の場合、特に規定なし */}
                    {campSite.checkout_start_hour !== '' &&
                      campSite.checkout_start_minute !== '' && (
                        <>
                          <Grid item xs={12}>
                            <Divider />
                          </Grid>
                          <Grid item xs={4.1}>
                            <Typography
                              fontSize={14}
                              fontWeight={700}
                              whiteSpace="nowrap"
                              variant="h2"
                            >
                              チェックアウト
                            </Typography>
                          </Grid>
                          <Grid item xs={7.9}>
                            {campSite.checkout_start_hour === '--' &&
                            campSite.checkout_start_minute === '--' &&
                            campSite.checkout_end_hour === '--' &&
                            campSite.checkout_end_minute === '--' ? (
                              <Typography fontSize={14} fontWeight={500}>
                                アウトフリー
                              </Typography>
                            ) : (
                              <Stack>
                                {campSite.checkout_start_hour !== '--' &&
                                  campSite.checkout_start_minute !== '--' && (
                                    <Typography
                                      fontSize={14}
                                      fontWeight={500}
                                      width={40}
                                      textAlign="center"
                                    >
                                      {campSite.checkout_start_hour}:
                                      {campSite.checkout_start_minute}
                                    </Typography>
                                  )}
                                <Typography
                                  fontSize={14}
                                  fontWeight={500}
                                  margin={`0 ${theme.spacing(1)}`}
                                >
                                  〜
                                </Typography>
                                {campSite.checkout_end_hour !== '--' &&
                                  campSite.checkout_end_minute !== '--' && (
                                    <Typography
                                      fontSize={14}
                                      fontWeight={500}
                                      width={40}
                                    >
                                      {campSite.checkout_end_hour}:
                                      {campSite.checkout_end_minute}
                                    </Typography>
                                  )}
                              </Stack>
                            )}
                          </Grid>
                          <Grid item xs={12}>
                            <Divider />
                          </Grid>
                        </>
                      )}
                  </Grid>
                  <Box paddingBottom={5}>
                    <Typography
                      fontSize={16}
                      fontWeight={700}
                      paddingBottom={1}
                      variant="h2"
                    >
                      お風呂・温泉施設
                    </Typography>
                    <Divider />

                    <Grid container spacing={2} paddingTop={2}>
                      {constBath.map((item, index) => {
                        const checked =
                          // @ts-ignore
                          (campSite[item.value] ?? false) === true;
                        return (
                          <Grid item xs={6} key={item.value + index}>
                            <Item checked={checked}>
                              <Typography fontSize={14} fontWeight={500}>
                                {item.label}
                              </Typography>
                              {checked ? (
                                <Icon iconType="circle" />
                              ) : (
                                <Icon iconType="minus" />
                              )}
                            </Item>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                  <Box paddingBottom={5}>
                    <Typography
                      fontSize={16}
                      fontWeight={700}
                      paddingBottom={1}
                      variant="h2"
                    >
                      場内設備
                    </Typography>
                    <Divider />
                    <CheckBoxList
                      items={ConstSearch.details[0]?.children ?? []}
                    />
                  </Box>
                  <Box paddingBottom={5}>
                    <Typography
                      fontSize={16}
                      fontWeight={700}
                      paddingBottom={1}
                      variant="h2"
                    >
                      お役立ちサービス・条件
                    </Typography>
                    <Divider />
                    <CheckBoxList
                      items={ConstSearch.details[1]?.children ?? []}
                    />
                  </Box>
                  <Box paddingBottom={5}>
                    <Stack alignEnd space={1} paddingBottom={1}>
                      <Typography fontSize={16} fontWeight={700} variant="h2">
                        体験・遊び・アクティビティ
                      </Typography>
                    </Stack>
                    <Divider />
                    <CheckBoxList
                      items={ConstSearch.details[2]?.children ?? []}
                    />
                  </Box>
                  <Typography
                    fontSize={18}
                    fontWeight={700}
                    textAlign="center"
                    paddingBottom={theme.spacing(3)}
                    variant="h2"
                  >
                    施設概要
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={3}>
                      <Typography fontSize={14} fontWeight={700}>
                        施設名
                      </Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography fontSize={14} fontWeight={500}>
                        {campSite.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={3}>
                      <Typography fontSize={14} fontWeight={700}>
                        住所
                      </Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography
                        fontSize={14}
                        fontWeight={500}
                        paddingBottom={'12px'}
                      >
                        {prefecturesCodeToString(campSite.prefectures_code)}{' '}
                        {campSite.address}
                      </Typography>
                      <Button
                        buttonSize="sm"
                        sx={{
                          padding: 0,
                          width: '136px',
                          borderRadius: '15px',
                          background: '#E2EDF5',
                        }}
                      >
                        <a
                          href={`https://www.google.co.jp/maps/place/${prefecturesCodeToString(
                            campSite.prefectures_code
                          )}${campSite.address}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Typography
                            fontSize={14}
                            fontWeight={500}
                            color="primary"
                          >
                            地図アプリで見る
                          </Typography>
                        </a>
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    {campSite.tel_head && (
                      <>
                        {' '}
                        <Grid item xs={3}>
                          <Typography fontSize={14} fontWeight={700}>
                            TEL
                          </Typography>
                        </Grid>
                        <Grid item xs={9}>
                          <Typography
                            fontSize={14}
                            fontWeight={500}
                            color="primary"
                          >
                            {campSite.tel_head}-{campSite.tel_while}-
                            {campSite.tel_bottom}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Divider />
                        </Grid>
                      </>
                    )}
                    {campSite.official_url && (
                      <>
                        <Grid item xs={3}>
                          <Typography fontSize={14} fontWeight={700}>
                            公式HP
                          </Typography>
                        </Grid>
                        <Grid item xs={9}>
                          <a
                            href={campSite.official_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Typography
                              fontSize={14}
                              fontWeight={500}
                              color="primary"
                              ellipsis={1}
                              sx={{ wordBreak: 'break-word' }}
                            >
                              {campSite.official_url}
                            </Typography>
                          </a>
                        </Grid>
                        <Grid item xs={12}>
                          <Divider />
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <Stack center paddingTop={3} paddingBottom={6}>
                    {campSite.official_url ? (
                      <a
                        href={`${campSite.official_url}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Button
                          buttonSize="md"
                          color="secondary"
                          endIcon={<Icon iconType="link" color="white" />}
                          sx={{ width: '246px' }}
                        >
                          公式・関連サイトから予約確認
                        </Button>
                      </a>
                    ) : (
                      <Button
                        buttonSize="md"
                        color="secondary"
                        endIcon={<Icon iconType="link" color="white" />}
                        sx={{ width: '246px' }}
                        disabled
                      >
                        公式・関連サイトから予約確認
                      </Button>
                    )}
                  </Stack>
                </Box>
                <CampSiteHistory campSiteId={campSite.id} />
                <Box sx={{ paddingBottom: '80px', background: '#ffffff' }} />
              </Box>
            </Detail>
          </DetailArea>
          <Stack
            between
            alignCenter
            space={3}
            sx={{
              position: 'fixed',
              bottom: 0,
              padding: theme.spacing(3),
              width: '100%',
              height: '80px',
              maxWidth: isPC ? '506px' : 'unset',
              background: '#e3e9e6',
            }}
          >
            <Button
              onClick={nextPage}
              buttonSize="md"
              color="primary"
              sx={{ width: '172px' }}
            >
              レビューを見る({campSite.total_review_num ?? '0'})
            </Button>
            <Stack space={1}>
              <Stack
                alignCenter
                column
                sx={{ justifyContent: 'space-between' }}
              >
                <Stack space={1}>
                  <IconButton
                    data-cypress-like-button
                    data-cypress-like={favorite}
                    disabled={
                      // ログイン状況のfetcheが完了しているかつ、
                      // ログイン中かつ、キャンプ場のお気に入り状況が取得できていない場合はdisabled（未ログイン場合はdesabledはfalse）
                      isFetched && isLogin && campSiteLikeListLoading
                    }
                    onClick={() => {
                      if (!isLogin) {
                        router.push('/login');
                        return;
                      }
                      if (!favorite) {
                        setDialogStatus({
                          ...dialogStatus,
                          open: true,
                          type: 'FAVORITE',
                          props: {
                            id: campSite.id,
                            totalLikeNum: campSite.total_like_num,
                            refetchCampSite: () => {
                              refetch();
                            },
                          },
                        });
                      } else {
                        useDeleteCampSiteLikeMutation
                          .fetcher({
                            campsite_id: campSite.id,
                            user_id: user!.id,
                          })()
                          .then(() => {
                            setFavorite(false);
                            refetch();
                          });
                      }
                    }}
                  >
                    <Icon iconType={favorite ? 'favorite' : 'not-favorite'} />
                  </IconButton>
                  <Typography
                    fontSize={18}
                    fontWeight={700}
                    color={favorite ? 'secondary' : 'gray'}
                  >
                    <span>{campSite.total_like_num}</span>
                  </Typography>
                </Stack>
                <Typography fontSize={10} color="gray" noWrap>
                  お気に入り
                </Typography>
              </Stack>
              <Stack
                alignCenter
                column
                sx={{ justifyContent: 'space-between' }}
              >
                <Switch
                  sx={{ marginTop: '-4px', marginBottom: '4px' }}
                  color="secondary"
                  checked={
                    !isLogin ? false : (visitedData?.visited ?? []).length > 0
                  }
                  onChange={(e) => {
                    if (!isLogin) {
                      router.push('/login');
                      return;
                    }
                    if (e.target.checked) {
                      useAddVisitedMutation
                        .fetcher({
                          campsite_id: campSite.id,
                          user_id: user!.id,
                        })()
                        .then(() => {
                          visitedRefetch();
                          triggerGetNotification();
                        });
                    } else {
                      useRemoveVisitedMutation
                        .fetcher({
                          campsite_id: campSite.id,
                          user_id: user!.id,
                        })()
                        .then(() => {
                          visitedRefetch();
                        });
                    }
                  }}
                />
                <Typography fontSize={10} color="gray">
                  行った！
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Footer>
      </Container>
    </>
  );
};

Index.getLayout = (page) => {
  return <Layout>{page}</Layout>;
};
export default Index;
export const getStaticPaths = async () => {
  const campSiteIds = await getCampSiteIdsOnStaticPaths();

  return {
    paths: campSiteIds.map((id) => ({
      params: {
        id,
      },
    })),
    fallback: 'blocking',
  };
};

export async function getStaticProps({ params }: any) {
  const campSite = await getCampSiteOnStaticProps(params.id);
  if (!campSite) {
    return {
      notFound: true,
    };
  }

  return {
    props: { queryResult: campSite, path: params.id },
    revalidate: getRevalidateSec(),
  };
}

const query = gql`
  query GetCampsiteIndex($id: String!) {
    campsite_by_pk(id: $id) {
      access_count
      activity_athletic
      activity_bbq
      activity_bicycle
      activity_canoe
      activity_craft
      activity_fishing
      activity_flower
      activity_hiking
      activity_insect
      activity_playset
      activity_pool
      activity_ranch
      activity_river
      activity_sauna
      activity_sea
      activity_star
      activity_taste
      activity_tentsauna
      address
      bath_hotspring
      bath_room
      business_newyear
      business_period
      checkin_start_hour
      checkin_start_minute
      checkin_end_hour
      checkin_end_minute
      checkout_start_hour
      checkout_start_minute
      checkout_end_hour
      checkout_end_minute
      closed_friday
      closed_irregular
      closed_holiday
      closed_monday
      closed_not
      closed_saturday
      campsite_images {
        campsite_id
        created_at
        id
        image_id
        type
        updated_at
        image {
          id
          alt
          created_at
          file_name
          title
          user_id
        }
      }
      closed_sunday
      closed_thursday
      closed_tuesday
      closed_wednesday
      created_at
      facilities_barrierfree
      facilities_dogrun
      facilities_laundry
      facilities_restaurant
      facilities_store
      facilities_workspace
      facility_acsite
      facility_bungalow
      facility_cottage
      facility_glamping
      facility_freesite
      facility_privatesite
      facility_solo
      facility_sectionsite
      facility_tipi
      facility_trailerhouse
      facility_treehouse
      id
      introduction_content
      introduction_title
      is_affiliated
      location_forest
      location_hill
      location_lake
      location_meadow
      location_ocean
      location_park
      image {
        user_id
        title
        id
        file_name
        created_at
        alt
      }
      location_river
      location_plateau
      main_image_id
      name
      official_url
      prefectures
      prefectures_code
      region_code
      service_firewood
      service_freecharge
      service_firework
      service_garbage
      service_group
      service_hotwater
      service_openfire
      service_pet
      service_rental
      service_washlet
      service_wifi
      tel_bottom
      tel_head
      tel_while
      total_like_num
      total_review_num
      total_visited_num
      updated_at
      vehicles_autocamp
      vehicles_campingcar
      visibility_status
      reviews(
        where: { public: { _eq: true } }
        order_by: { created_at: desc }
      ) {
        campsite_id
        content
        created_at
        id
        public
        review_goods {
          created_at
          for_review_id
          from_user_id
          id
        }
        review_goods_aggregate {
          aggregate {
            count
          }
        }
        total_good_num
        updated_at
      }
    }
  }
`;
